import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import styled from 'styled-components/macro';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  FormGroup,
  Box,
  ButtonBase,
  makeStyles,
  createStyles,
  Theme,
  Tooltip,
  IconButton,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { useDropzone } from 'react-dropzone';
import MatUiCheckbox from './MatUiCheckbox';
import SelectAllWithActionIcon from './SelectAllWithActionIcon';
import { FilenameHelper } from 'store/helpers';
import {
  alertsActions,
  fileActions,
  previewFileActions,
  ticketActions,
  showOverlayActions,
} from 'store/actions';
import { useParams } from 'react-router-dom';
import fileImage from 'assets/images/file_icon.png';
import loadingImg from 'assets/images/spinner.gif';
import ErrorIcon from '@material-ui/icons/Error';
import { ReactComponent as LockIcon } from 'assets/images/lock.svg';
import { ReactComponent as DropIcon } from 'assets/images/Group.svg';
import {
  ApiAttachmentForFS,
  FormattedMainFile,
  MainFiles as MFS,
  MainFile as MF,
} from 'store/models/MainFiles';
import googleDriveIconImg from 'assets/images/google-drive-logo.svg';
import { canEditTicketSelector } from 'store/selectors/ticket.selector';
import { ticketApiWithFilesSelector } from 'store/selectors/ticket-attachment.selector';
import { merge } from 'lodash';
import PreviewFileHelper from 'store/models/PreviewFileHelper';
import InfoIcon from '@material-ui/icons/Info';
import { ReactComponent as Arrow } from '../../assets/images/headArrow.svg';
import { ReactComponent as Trash } from '../../assets/images/trash.svg';
import RestoreIcon from '@material-ui/icons/Restore';

// NOTE: not sure if ticketDataSelector should be in the MainPage class or not

interface MainFilesProps {
  isAvailableFiles?: boolean;
  supTitle: string;
  title: string;
  hasChangeOrSaveClickedRef: { current: boolean };
}
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    removeButton: {
      marginRight: theme.spacing(3),
    },
  }),
);

const isDeletingFilesSelector = (state) =>
  state.ticket.deletingPartIds && state.ticket.deletingPartIds.length;

const MainFiles: React.FC<MainFilesProps> = ({
  isAvailableFiles = true,
  supTitle,
  title,
  hasChangeOrSaveClickedRef,
}) => {
  const [openAccordion, setOpenAccordion] = useState(
    isAvailableFiles,
  );
  const { ticketId } = useParams<{ ticketId: string }>();

  const canEdit = useSelector(canEditTicketSelector);

  const dispatch = useDispatch();
  const classes = useStyles();

  const { files: filesFromSelector } = useSelector(
    ticketApiWithFilesSelector,
  );

  const isDeletingFiles = useSelector(isDeletingFilesSelector);
  const [files, setFiles] = useState<FormattedMainFile[]>(
    filesFromSelector,
  );
  const relatedFiles: FormattedMainFile[] = useMemo(() => {
    const recycledFilter = !isAvailableFiles;
    return (
      files?.filter((e) => {
        return !!e.isRecycled === recycledFilter;
      }) ?? []
    );
  }, [files, isAvailableFiles]);
  // for checking files from selector change in useEffect dependency
  const filesFromSelectorString = useMemo(
    () => filesFromSelector && JSON.stringify(filesFromSelector),
    [filesFromSelector],
  );
  const hasAnySelected = useMemo(() => MFS.hasAnySelected(files), [
    files,
  ]);

  /**
   * handleFilesUploaded
   * @param e
   */
  const handleDragFile = async (e) => {
    setOnDrag(false);
    const promises: any = [];
    e.forEach((e) => {
      let file = e;
      const existedFNames = files.map((f) => f.filename);
      const newName = FilenameHelper.getUploadedFilename(
        file.name,
        existedFNames,
      );
      // upload file
      promises.push(
        dispatch(fileActions.upload(file, newName, ticketId)),
      );
    });
    await Promise.all(promises);
    dispatch(ticketActions.loadTicket(ticketId, 'AFTER_UPLOAD_FILE'));
    setSelectedAll(false);
  };

  /**
   * note: for now as of 2020-09-09 can only do one file
   * @param e
   */
  const handleFilesUploaded = async (e) => {
    const uploadedFiles: FileList = e.target.files;
    const length = uploadedFiles.length;
    const promises: any = [];
    console.debug(
      'handleFilesUploaded files',
      uploadedFiles,
      'length',
      length,
      'existed files',
      files,
    );
    if (length > 0) {
      Array.from(uploadedFiles).forEach((file) => {
        // check for duplicate name with the old files
        // rename if necessary
        const existedFNames = files.map((f) => f.filename);
        const newName = FilenameHelper.getUploadedFilename(
          file?.name,
          existedFNames,
        );
        // will have to use reducer here
        if (file) {
          promises.push(
            dispatch(fileActions.upload(file, newName, ticketId)),
          );
          setSelectedAll(false);
        }
      });
      await Promise.all(promises);
      dispatch(
        ticketActions.loadTicket(ticketId, 'AFTER_UPLOAD_FILE'),
      );
      // otherwise won't be able to upload the same file name twice
      e.target.value = null;
    } else {
      console.warn('no files selected');
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // ---------------------------------------- old remove function
  // if (MF.isATicketFile(file)) {
  //   dispatch(ticketActions.deleteFiles([file.partId]));
  // } else {
  //   // for the failed to upload file -> no api call to delete
  // dispatch(
  //   fileActions.deleteLocalAtIndex(file.uploadingFilesIndex),
  // );
  // }
  // --------------------------------------------

  const handleToggleSelectFile = (file, checked: boolean) => {
    const updatedFiles = files.map((f) => {
      if (MF.isATicketFile(f)) {
        return file.partId === f.partId
          ? {
              ...f,
              isSelected: checked,
            }
          : f;
      } else {
        // if not a ticket file
        return file.uploadingFilesIndex === f.uploadingFilesIndex
          ? {
              ...f,
              isSelected: checked,
            }
          : f;
      }
    });
    setFiles(updatedFiles);
    // auto unselect all when unselect child
    if (!checked) {
      setSelectedAll(false);
    }
  };
  const getSelectedFiles = useCallback(
    (validOnly = false) => {
      if (!files) return [];
      const selected = files.filter((f) => f.isSelected);
      if (!validOnly) return selected;
      return selected.filter((f) => MF.isATicketFile(f)); // partId is not undefined
    },
    [files],
  );
  // check if there is any selected files has OCR. If not canClickOCR must be false.
  const canClickOCR = useMemo(() => {
    const selectedFiles = getSelectedFiles();
    const ocrs = selectedFiles.filter(
      (e) =>
        e?.ocr?.bookingNumbers?.length ||
        e?.ocr?.containerNumbers?.length ||
        e?.ocr?.sealNumbers?.length,
    );
    return ocrs.length > 0;
  }, [getSelectedFiles]);

  // const handleClickDeleteManyFiles = () => {
  //   const selected = getSelectedFiles();
  //   const ticketFiles = getSelectedFiles(true);
  //   const failedToUploadFiles = selected.filter(
  //     (f) => !MF.isATicketFile(f),
  //   );
  //   if (ticketFiles.length) {
  //     const partIds = ticketFiles.map((t) => t.partId);
  //     dispatch(ticketActions.deleteFiles(partIds));
  //   }
  //   if (failedToUploadFiles.length) {
  //     const indexes = failedToUploadFiles.map(
  //       (f) => f.uploadingFilesIndex,
  //     );
  //     dispatch(fileActions.deleteLocalAtIndexes(indexes));
  //   }
  // };
  const handleSwitchBetweenRestoreAndRecycleMany = async (
    isRecycled: boolean,
  ) => {
    const selected: FormattedMainFile[] = getSelectedFiles();
    const filesPartId = selected.map((e) => e.partId);
    handleToggleSelectAll(false);
    setSelectedAll(false);
    dispatch(
      ticketActions.updateFilesRecycleStatus(filesPartId, isRecycled),
    );
  };
  const handleSwitchBetweenRestoreAndRecycleFile = async (
    file,
    isRecycled,
  ) => {
    dispatch(
      ticketActions.updateFilesRecycleStatus(
        [file.partId],
        isRecycled,
      ),
    );
    handleToggleSelectFile(file, false);
  };

  const [isSelectedAll, setSelectedAll] = useState(false);

  useEffect(() => {
    console.debug(`filesFromSelector changed: `, filesFromSelector);
    let toUpdate = filesFromSelector;
    setFiles((curFiles) => {
      if (filesFromSelector) {
        const newPartIdSelect = filesFromSelector.reduce(
          (acc, file) => ({ ...acc, [file.partId]: file.isSelected }),
          {},
        );
        const oldPartIdSelect =
          curFiles?.reduce(
            (acc, file) => ({
              ...acc,
              [file.partId]: file.isSelected,
            }),
            {},
          ) ?? {};
        const merged = merge(newPartIdSelect, oldPartIdSelect);
        toUpdate = toUpdate.map((file) => ({
          ...file,
          isSelected: merged[file.partId],
        }));
      }
      return toUpdate;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesFromSelectorString, isSelectedAll]);

  const handleToggleSelectAll = (checked: boolean) => {
    setSelectedAll(checked);
    if (!files) return;
    const newFiles = files.map((f) => {
      if (isAvailableFiles) {
        return !f.isRecycled ? { ...f, isSelected: checked } : f;
      } else {
        return f.isRecycled ? { ...f, isSelected: checked } : f;
      }
    });
    setFiles(newFiles);
  };

  const handlePreview = (file: FormattedMainFile) => {
    dispatch(previewFileActions.previewFile(file));
  };

  const [onDrag, setOnDrag] = useState(false);
  const {
    getRootProps,
    // acceptedFiles
  } = useDropzone({
    noClick: true,
    onDrop: (e) => handleDragFile(e),
    onDragOver: () => setOnDrag(true),
    onDragLeave: () => setOnDrag(false),
  });
  const handleClickGoogleDrive = (file: FormattedMainFile) => {
    if (file.driveFileId) {
      const win = window.open(
        `https://docs.google.com/spreadsheets/d/${file.driveFileId}`,
        '_blank',
        'noopener,noreferrer',
      );
      if (win) win.focus();
    } else {
      dispatch(alertsActions.error(`Missing driveFileId`));
    }
  };

  const handleClickOCR = () => {
    console.debug('show OCR');
    const selectedFiles = getSelectedFiles(true);
    console.log('selectedFiles', selectedFiles);
    dispatch(showOverlayActions.show('OCR', { selectedFiles }));
  };

  const relatedFilesCountStr = useMemo(() => {
    if (!relatedFiles) return '';
    return `(${relatedFiles.length})`;
  }, [relatedFiles]);

  return (
    <SMainFiles>
      <SHeaderContainer
        onClick={() => setOpenAccordion(!openAccordion)}
        className={isAvailableFiles ? 'files' : 'recycle-bin'}
      >
        <div>
          <SSectionTitle withoutMarginBottom={false}>
            {`${title} ${relatedFilesCountStr}`}
          </SSectionTitle>
          <SDragAndDropText>{supTitle}</SDragAndDropText>
        </div>
        <SIcon className={openAccordion ? 'open' : 'close'}>
          <Arrow />
        </SIcon>
      </SHeaderContainer>
      {openAccordion && (
        <div>
          <SSelectAndAdd>
            <SSelectAndOCR>
              <SelectAllWithActionIcon
                handleToggleSelectAll={handleToggleSelectAll}
                handleClickActionMany={() =>
                  handleSwitchBetweenRestoreAndRecycleMany(
                    isAvailableFiles,
                  )
                }
                isActioningMany={isDeletingFiles}
                isSelectedAll={isSelectedAll}
                disabled={!canEdit || !relatedFiles.length}
                hideIcon={!hasAnySelected} // do not show delete button if do not selected any files
                icon={
                  isAvailableFiles ? undefined : (
                    <RestoreIcon fontSize="small" />
                  )
                }
              />
              {/* OCR icon */}
              {hasAnySelected && (
                <IconButton
                  color="primary"
                  // disable if there is no OCR on any attachments
                  onClick={() => canClickOCR && handleClickOCR()}
                >
                  <InfoIcon
                    color={canClickOCR ? 'primary' : 'disabled'}
                    fontSize="small"
                  />
                </IconButton>
              )}
            </SSelectAndOCR>
            <SHeader>Booking No.</SHeader>
            <SHeader>Container Number</SHeader>
            {isAvailableFiles && (
              <SAddFile>
                <input
                  hidden
                  id="add-file-button"
                  multiple
                  type="file"
                  onChange={(e) => {
                    hasChangeOrSaveClickedRef.current = true;
                    handleFilesUploaded(e);
                  }}
                  disabled={!canEdit}
                />
                <Button
                  color="primary"
                  variant="contained"
                  startIcon={<AddIcon />}
                  component="label"
                  htmlFor="add-file-button"
                  disabled={!canEdit}
                >
                  Add Files
                </Button>
              </SAddFile>
            )}
          </SSelectAndAdd>
          <section className="container">
            <SSelectFilesAndAddFile>
              <SDropZone
                {...(canEdit && isAvailableFiles
                  ? getRootProps({ className: 'dropzone' })
                  : {})}
                className={`${onDrag ? 'onDrag' : ''} ${
                  relatedFiles && relatedFiles.length === 0
                    ? 'noFile'
                    : ''
                } ${isAvailableFiles ? 'files' : 'recycle'}`}
              >
                {relatedFiles && relatedFiles.length === 0 && (
                  <EmptyZone isAvailableFiles={isAvailableFiles} />
                )}
                <SSelectFiles>
                  {relatedFiles && (
                    <FormGroup>
                      {relatedFiles.map((file) => {
                        const fileM = new PreviewFileHelper(file);
                        return (
                          <SEachFile
                            key={
                              file.partId ||
                              `uploading-${file.uploadingFilesIndex}`
                            }
                          >
                            <SBoxAndName>
                              <MatUiCheckbox
                                value={file.isSelected}
                                handleChange={(checked: boolean) =>
                                  handleToggleSelectFile(
                                    file,
                                    checked,
                                  )
                                }
                                disabled={!canEdit || file.isLoading}
                                name={file.partId}
                                label={
                                  <Box display="flex">
                                    {file.hasParent && (
                                      <SIndent>
                                        <SLine></SLine>
                                      </SIndent>
                                    )}
                                    <LoadingOrFileImage file={file} />
                                  </Box>
                                }
                              />
                              {/* file */}

                              {/* filename file */}

                              <SFileNameAndGoogleDrive>
                                <SBox
                                  className={`clickable ${
                                    fileM.isViewOrDownloadable()
                                      ? 'view-downloadable'
                                      : ''
                                  } ${file.locked ? 'lock' : ''} ${
                                    fileM.hasGDriveLink()
                                      ? 'with-gdrive'
                                      : ' '
                                  }`}
                                  onClick={() => handlePreview(file)}
                                  title={file.filename} // show filename on hover
                                >
                                  {file.filename}
                                  <SGoogleDriveWrapper
                                    onClick={() =>
                                      handleClickGoogleDrive(file)
                                    }
                                  >
                                    <SGoogleDriveIcon>
                                      <img
                                        src={googleDriveIconImg}
                                        width="15px"
                                        alt="google-drive"
                                      />
                                    </SGoogleDriveIcon>
                                  </SGoogleDriveWrapper>
                                </SBox>

                                {/* booking number, container number column from OCR */}
                                {[
                                  file?.ocr?.bookingNumbers,
                                  file?.ocr?.containerNumbers,
                                ].map((e, i) => {
                                  return (
                                    <SNumber
                                      key={`ocr-info-${i}`}
                                      style={{ marginRight: '1vw' }}
                                    >
                                      {e?.length ? (
                                        <div>
                                          <div>{e[0]}</div>
                                          <SCount>{e?.length}</SCount>
                                        </div>
                                      ) : (
                                        <div>No result found</div>
                                      )}
                                    </SNumber>
                                  );
                                })}
                              </SFileNameAndGoogleDrive>
                              {file.locked && <LockIcon />}
                            </SBoxAndName>
                            {/* disabled the remove button if isLoading */}
                            <SRemoveButtonButton
                              disabled={!canEdit || file.isLoading}
                              className={classes.removeButton}
                              onClick={() =>
                                handleSwitchBetweenRestoreAndRecycleFile(
                                  file,
                                  isAvailableFiles,
                                )
                              }
                              color="primary"
                              variant="text"
                            >
                              -{' '}
                              {isAvailableFiles
                                ? 'Remove'
                                : 'Restore'}
                            </SRemoveButtonButton>
                          </SEachFile>
                        );
                      })}
                    </FormGroup>
                  )}
                </SSelectFiles>
              </SDropZone>
            </SSelectFilesAndAddFile>
          </section>
        </div>
      )}
    </SMainFiles>
  );
};

export const renderFileImage = (
  file: FormattedMainFile | ApiAttachmentForFS,
  width = '20px',
) => {
  let title = '';
  let className = '';

  if (!file.processed) {
    title = `unprocessed file`;
    className = 'not-processed';
  } else if ((file as any).canSelectPage === false) {
    title = `cannot select page`;
    className = 'cannot-select-page';
  }

  return (
    <Tooltip title={title}>
      <SFileImage
        src={fileImage}
        width={width}
        className={className}
        alt={file.mimeType || file.filename}
      />
    </Tooltip>
  );
};

const EmptyZone = ({ isAvailableFiles }) =>
  !isAvailableFiles ? (
    <>
      <Trash />
      <SText>Trash is empty</SText>
      <SSubText>Deleted file will appear here</SSubText>
    </>
  ) : (
    <>
      <DropIcon />
      <SText>Drop files here</SText>
      <SSubText>or use the “Add Files” button</SSubText>
    </>
  );

const LoadingOrFileImage = ({
  file,
}: {
  file: FormattedMainFile;
}) => {
  if (file.isLoading) {
    return <img src={loadingImg} width="20px" alt="loading" />;
  } else if (file.error) {
    return <ErrorIcon fontSize="small" color="error" />;
  }
  return renderFileImage(file);
};

export default MainFiles;
const SIcon = styled.div`
  &.open {
    transform: rotate(180deg);
  }
  &.close {
    transform: rotate(0deg);
  }
`;
const SSectionTitle = styled.div`
  border-left: 6px solid
    ${(props) => props.theme.palette.primary.main};
  padding: 0 0.5rem;
  font-size: ${(props) => props.theme.typography.fontSize.title};
  font-weight: ${(props) => props.theme.typography.fontWeight.title};
  margin-bottom: ${(props: { withoutMarginBottom: boolean }) =>
    props.withoutMarginBottom ? '0' : '0.75rem'};
`;
const SHeaderContainer = styled.div`
  cursor: pointer;
  display: flex;
  padding: 10px 20px;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  & > div > svg > path {
    fill: ${(props) => props.theme.palette.primary.main};
  }
  &.files {
    background: ${(props) => props.theme.palette.primary.light};
    border-radius: 6px;
  }
  &.recycle-bin {
    background: ${(props) => props.theme.palette.common.white};
    border: 1px solid ${(props) => props.theme.palette.primary.main};
    box-sizing: border-box;
    border-radius: 6px;
  }
`;
const SHeader = styled.div`
  display: flex;
  align-items: center;
  font-weight: bold;
  width: 15vw;
  white-space: nowrap;
  overflow: hidden;
`;
const SNumber = styled.div`
  width: 14vw;
  margin-right: 1vw;

  & > div {
    display: flex;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
  }
`;
const SCount = styled.div`
  width: 20px;
  height: 20px;
  background-color: ${(props) => props.theme.palette.primary.light};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => props.theme.palette.primary.main};
  margin-left: 5px;
`;

const SGoogleDriveWrapper = styled.div`
  /* position: relative; */
  display: none;
  min-width: 200px;
  overflow: visible;
  position: absolute;
  margin: 5px 0px 0px 8px;
`;
const SFileNameAndGoogleDrive = styled.div.attrs((props) => ({
  className: props.className,
}))`
  display: flex;
  position: relative;
`;
const SGoogleDriveIcon = styled(ButtonBase)`
  &.MuiButtonBase-root {
    position: absolute;
    top: -1rem;
    background: ${(props) => props.theme.palette.background.paper};

    border: 1px solid
      ${(props) => props.theme.palette.common.borderGrey};
    border-radius: 50%;
    padding: 0.5rem;
    margin: 4px 0;
    box-shadow: 0px 0px 8px 1px rgb(251 251 251);
    transition: all 0.2s, border-radius 0s;

    &:hover {
      border: 1px solid #ced4da;
      border-radius: 50%;
      border-radius: 1rem;
      display: flex;
      justify-content: flex-start;
      height: 2rem;
      padding-left: 1rem;
      padding-right: 1rem;
      &::after {
        margin-left: 10px;
        content: 'Open in Google Drive';
        // text-decoration: underline;
        color: ${(props) => props.theme.palette.text.primary};
      }
      transition: all 0.2s ease-out, border-radius 0s;
    }
  }
`;

const SFileImage = styled.img.attrs((props) => ({
  className: props.className,
}))`
  &.not-processed {
    filter: grayscale(90%);
  }
  &.cannot-select-page {
    filter: hue-rotate(223deg);
  }
`;

const SSelectAndAdd = styled.div`
  display: flex;
  width: 100%;
`;

const SSelectAndOCR = styled.div`
  display: flex;
  width: 28vw;
`;
const SText = styled.div`
  margin-top: 10px;
`;
const SSubText = styled.div`
  margin-top: 5px;
`;
const SDragAndDropText = styled.div`
  color: ${(props) => props.theme.palette.primary.lightgrey};
  font-style: italic;
  margin-left: 15px;
`;
const SDropZone = styled.div`
  margin-left: -5px;
  width: 63.5vw;
  border: 2px solid white;
  border-radius: 8px;
  padding-left: 5px;

  &.noFile {
    &.files {
      background: ${(props) =>
        props.theme.palette.background.lightPink};
      color: ${(props) => props.theme.palette.primary.greylight};
    }
    &.recycle {
      background: ${(props) =>
        props.theme.palette.background.default};
      color: ${(props) => props.theme.palette.secondary.dark};
    }
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-weight: 500;
    line-height: 14px;
    padding: 20px;
  }
  &.onDrag {
    background: rgba(70, 125, 206, 0.15);
    border: 2px solid ${(props) => props.theme.palette.highlight.blue};
  }
`;
const SIndent = styled.div`
  margin-right: 5px;
  width: 1.3rem;
  display: grid;
  place-items: center;
`;
const SLine = styled.div`
  width: 100%;
  height: 1px;
  background: ${(props) => props.theme.palette.grey[300]};
`;
const SBox = styled(Box)`
  margin-left: 9px;
  font-weight: ${(props) => props.theme.typography.fontWeight.title};
  margin-right: 1vw;
  white-space: nowrap;
  overflow: hidden;
  width: 22.5vw;
  text-overflow: ellipsis;
  display: inline;
  &.with-gdrive {
    // &:hover > ${SGoogleDriveWrapper} {
    //   display: unset;
    // }
    & > ${SGoogleDriveWrapper} {
      display: unset;
    }
  }
  &.lock {
    color: ${(props) => props.theme.palette.secondary.dark};
  }
  &.view-downloadable {
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SBoxAndName = styled.div`
  display: flex;
  align-items: center;

  & > svg > path {
    fill: ${(props) => props.theme.palette.secondary.dark};
  }
`;

const SMainFiles = styled.div`
  margin-top: 1.5rem;
  display: block;
`;

const SSelectFilesAndAddFile = styled.div`
  margin-top: 4px;
  display: flex;
  justify-content: space-between;
`;
const SSelectFiles = styled.div`
  flex: 1;
  padding-top: 8px;
  & .MuiCheckbox-root {
    padding: 2px;
    margin: 0 7px; // to match select all level
  }
  & .MuiFormControlLabel-label {
    font-weight: 600;
  }
  height: 20vh;
  width: 65.5vw;
  overflow-y: auto;
`;
const SAddFile = styled.div`
  flex: 0 0 auto;
  display: flex;
  height: 44px;
  justify-content: flex-end;
`;
const SEachFile = styled.div`
  display: flex;
  align-items: center;
  // display: grid;
  // grid-template-columns: 1fr auto;
  // align-items: center;
  border-bottom: 1px dashed
    ${(props) => props.theme.palette.grey[300]}; // or #e8e8e8
`;
const SRemoveButtonButton = styled(Button)`
  white-space: nowrap;
  text-align: end;
  width: 10vw;
`;
